import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Volume2, VolumeX } from "lucide-react";
import { useWrappedStats } from "@/hooks/useWrappedStats";
import { useWrappedAnalytics } from "@/hooks/useWrappedAnalytics";
import { useProfile } from "@/hooks/useProfile";
import { useWrappedMusic } from "@/hooks/useWrappedMusic";
import IntroSlide from "@/components/wrapped/IntroSlide";
import TotalEventsSlide from "@/components/wrapped/TotalEventsSlide";
import BestiesSlide from "@/components/wrapped/BestiesSlide";
import TopHostSlide from "@/components/wrapped/TopHostSlide";
import StreakSlide from "@/components/wrapped/StreakSlide";
import GalleriesSlide from "@/components/wrapped/GalleriesSlide";
import SalonsSlide from "@/components/wrapped/SalonsSlide";
import SummarySlide from "@/components/wrapped/SummarySlide";
import Snowfall from "@/components/wrapped/Snowfall";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import ShareButton from "@/components/wrapped/ShareButton";
import { capture as posthogCapture } from "@/services/posthog";
const SLIDES = [
  "intro",
  "total-events",
  "besties",
  "top-host",
  "streak",
  "galleries",
  "salons",
  "summary",
] as const;

const Wrapped = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideRef = useRef<HTMLDivElement>(null);
  const { data: stats, isLoading: statsLoading } = useWrappedStats(2025);
  const { data: profile, isLoading: profileLoading } = useProfile();
  const { trackSlideView, trackShareClick } = useWrappedAnalytics(2025);

  const isLoading = statsLoading || profileLoading;
  const { isMuted, toggleMute } = useWrappedMusic();

  useEffect(() => {
    posthogCapture("wrapped_opened", { year: 2025 });
  }, []);

  // Track slide views
  useEffect(() => {
    if (stats) {
      trackSlideView(SLIDES[currentSlide]);
    }
  }, [currentSlide, stats, trackSlideView]);

  const goNext = () => {
    if (currentSlide < SLIDES.length - 1) {
      setCurrentSlide((prev) => prev + 1);
    }
  };

  const goPrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight" || e.key === " ") {
      goNext();
    } else if (e.key === "ArrowLeft") {
      goPrev();
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-primary flex items-center justify-center">
        <LoadingSpinner className="border-secondary" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="fixed inset-0 bg-primary flex items-center justify-center p-6">
      <div className="text-center text-background">
          <h1 className="text-2xl font-bold mb-4">Your 2025 Wrapped</h1>
          <p className="text-background/80">
            Your Year in Art is being prepared! Check back soon.
          </p>
        </div>
      </div>
    );
  }

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? "100%" : "-100%",
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? "100%" : "-100%",
      opacity: 0,
    }),
  };

  const renderSlide = () => {
    const firstName = profile?.first_name || "Art Lover";
    
    switch (SLIDES[currentSlide]) {
      case "intro":
        return <IntroSlide firstName={firstName} />;
      case "total-events":
        return (
          <TotalEventsSlide
            totalEvents={stats.total_events}
            monthlyActivity={stats.monthly_activity}
          />
        );
      case "besties":
        return <BestiesSlide besties={stats.top_besties} />;
      case "top-host":
        return <TopHostSlide host={stats.top_host} />;
      case "streak":
        return (
          <StreakSlide
            longestStreak={stats.longest_streak}
            streakDetails={stats.streak_details}
          />
        );
      case "galleries":
        return <GalleriesSlide count={stats.galleries_visited} />;
      case "salons":
        return <SalonsSlide count={stats.secret_salons_visited} />;
      case "summary":
        return (
          <SummarySlide
            stats={stats}
            firstName={firstName}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 bg-primary overflow-hidden select-none"
      onKeyDown={handleKeyDown}
      tabIndex={0}
      onClick={goNext}
    >

      {/* Progress dots */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {SLIDES.map((_, idx) => (
          <button
            key={idx}
            onClick={(e) => {
              e.stopPropagation();
              setCurrentSlide(idx);
            }}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === currentSlide
                ? "bg-secondary w-6"
                : "bg-background/30 hover:bg-background/50"
            }`}
          />
        ))}
      </div>

      {/* Share button */}
      <ShareButton 
        slideRef={slideRef} 
        slideIndex={currentSlide} 
        onShare={() => trackShareClick(SLIDES[currentSlide])}
      />

      {/* Music control */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleMute();
        }}
        className="absolute top-4 right-4 z-20 p-2 rounded-full bg-background/10 hover:bg-background/20 transition-colors"
        aria-label={isMuted ? "Unmute music" : "Mute music"}
      >
        {isMuted ? (
          <VolumeX className="w-5 h-5 text-background" />
        ) : (
          <Volume2 className="w-5 h-5 text-background" />
        )}
      </button>

      {/* Navigation arrows */}
      {currentSlide > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-background/10 hover:bg-background/20 transition-colors"
        >
          <ChevronLeft className="w-6 h-6 text-background" />
        </button>
      )}
      {currentSlide < SLIDES.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-background/10 hover:bg-background/20 transition-colors"
        >
          <ChevronRight className="w-6 h-6 text-background" />
        </button>
      )}

      {/* Slide content with snowfall for capture */}
      <AnimatePresence mode="wait" custom={1}>
        <motion.div
          key={currentSlide}
          custom={1}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="absolute inset-0 flex items-center justify-center"
        >
          {/* Capture wrapper includes background, snow, and content */}
          <div ref={slideRef} className="absolute inset-0 bg-primary overflow-hidden">
            <Snowfall />
            <div className="absolute inset-0 flex items-center justify-center">
              {renderSlide()}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Skip button */}
      {currentSlide < SLIDES.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setCurrentSlide(SLIDES.length - 1);
          }}
          className="absolute bottom-8 right-4 z-20 text-sm text-background/60 hover:text-background/80 transition-colors"
        >
          Skip to end →
        </button>
      )}
    </div>
  );
};

export default Wrapped;
